import React, { useState, useEffect } from 'react';
import { GrNotification } from "react-icons/gr";
import { Link, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import notificationSound from '../assets/sounds/sound.mp3';

const BACK_END_URL = 'https://shtato-b15797a0a9a3.herokuapp.com/';

function Navbar() {
    const navigate = useNavigate();
    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState(() => {
        const saved = localStorage.getItem('notifications');
        return saved ? JSON.parse(saved) : [];
    });
    const [showNotifications, setShowNotifications] = useState(false);
    const [unreadCount, setUnreadCount] = useState(() => {
        const saved = localStorage.getItem('notifications');
        return saved ? JSON.parse(saved).filter(n => !n.read).length : 0;
    });

    useEffect(() => {
        const newSocket = io(BACK_END_URL);
        setSocket(newSocket);
        return () => newSocket.close();
    }, []);

    useEffect(() => {
        localStorage.setItem('notifications', JSON.stringify(notifications));
    }, [notifications]);

    const formatTime = (time) => {
        const hours = Math.floor(time);
        const minutes = (time % 1) * 60;
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes === 0 ? '00' : '30'}`;
        return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const playNotificationSound = () => {
        const audio = new Audio(notificationSound);
        audio.play().catch(error => console.error("Error playing sound:", error));
    };

    useEffect(() => {
        if (!socket) return;

        const unlockAudio = () => {
            const audio = new Audio();
            audio.play().catch(() => console.log("Audio unlocked."));
            document.removeEventListener("click", unlockAudio);
        };

        document.addEventListener("click", unlockAudio);

        socket.on('newReservation', (data) => {
            const newNotification = {
                id: Date.now(),
                message: `New reservation for ${data.zone} at ${formatTime(data.time)}`,
                timestamp: new Date(),
                read: false,
                zone: data.zone
            };
            setNotifications(prev => [newNotification, ...prev]);
            setUnreadCount(prev => prev + 1);
            playNotificationSound();
        });

        return () => socket.off('newReservation');
    }, [socket]);

    const handleNotificationClick = (notification) => {
        markAsRead(notification.id);
        setShowNotifications(false);
        navigate('/reservations', { state: { highlightZone: notification.zone } });
    };

    const markAsRead = (id) => {
        setNotifications(prev =>
            prev.map(notif =>
                notif.id === id ? { ...notif, read: true } : notif
            )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const markAllAsRead = () => {
        setNotifications(prev =>
            prev.map(notif => ({ ...notif, read: true }))
        );
        setUnreadCount(0);
    };

    const clearAll = () => {
        setNotifications([]);
        setUnreadCount(0);
        localStorage.removeItem('notifications');
    };

    return (
        <nav className='flex sm:px-10 px-6 py-3 bg-[#F9F9F9] font-[sans-serif] min-h-[70px] ml-0 lg:ml-56'>
            <div className="text-end flex w-full max-w-screen-xl mx-auto">
                <div className='flex flex-wrap items-center justify-between lg:gap-y-4 gap-y-4 gap-x-4 w-full'>
                    <div className="ml-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        <p className="text-base text-[#3E435D] font-semibold">
                            Welcome Back, Shahin <span className='text-sm text-[#ADA7A7] mt-0.5'>
                                {new Date().toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric'
                                }).replace(',', ', ')}
                            </span>
                        </p>
                    </div>

                    <div className='flex items-center space-x-4 max-md:ml-auto'>
                        <div className="relative">
                            <button
                                onClick={() => setShowNotifications(!showNotifications)}
                                className="border-none outline-none flex items-center justify-center rounded-lg p-2 bg-white hover:bg-gray-100 transition-all"
                            >
                                <GrNotification className="text-xl" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                        {unreadCount}
                                    </span>
                                )}
                            </button>

                            {showNotifications && (
                                <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg z-50 overflow-hidden">
                                    <div className="p-4 border-b bg-gray-100">
                                        <div className="flex justify-between items-center">
                                            <h3 className="font-semibold text-gray-800">Notifications</h3>
                                            <div className="flex gap-2">
                                                {unreadCount > 0 && (
                                                    <button
                                                        onClick={markAllAsRead}
                                                        className="px-4 py-2 text-sm text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 focus:outline-none transition"
                                                    >
                                                        Mark all read
                                                    </button>
                                                )}
                                                {notifications.length > 0 && (
                                                    <button
                                                        onClick={clearAll}
                                                        className="px-4 py-2 text-sm text-red-600 bg-red-100 rounded-lg hover:bg-red-200 focus:outline-none transition"
                                                    >
                                                        Clear all
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="max-h-96 overflow-y-auto">
                                        {notifications.length === 0 ? (
                                            <div className="p-4 text-center text-gray-500">
                                                No notifications
                                            </div>
                                        ) : (
                                            notifications.map(notification => (
                                                <div
                                                    key={notification.id}
                                                    className={`p-4 border-b hover:bg-gray-100 cursor-pointer rounded-lg transition-all duration-200 shadow-sm ${notification.read ? 'bg-white' : 'bg-blue-50'}`}
                                                    onClick={() => handleNotificationClick(notification)}
                                                >
                                                    <div className="flex items-center">
                                                        <div className={`w-2.5 h-2.5 rounded-full ${notification.read ? 'bg-gray-400' : 'bg-blue-600'} mr-2`}></div>
                                                        <p className="text-sm text-gray-800">{notification.message}</p>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {new Date(notification.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>

                        <Link to="/profile" className="border-none outline-none flex items-center justify-center rounded-full p-2 hover:bg-gray-100 transition-all">
                            <img src={`${BACK_END_URL}images/file_1111111111111.jpg`} className="w-10 h-10 rounded-full border-2 border-white" alt="logo" />
                        </Link>
                    </div>
                </div>
            </div>
        </nav>
    );
}

export default Navbar;
